import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Input,
  Button,
  Label,
  FormGroup,
  Alert,
  Table,
  Badge,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { BackgroundColorContext, backgroundColors } from '../../contexts/BackgroundColorContext';
import { useNotification } from '../../contexts/NotificationContext';
import AccountService from '../../service/account.service';
import WhatsappService from '../../service/whatsapp.service';

const VARIABLES = [
  { key: '{{nome_cliente}}', label: 'Nome do cliente' },
  { key: '{{telefone}}', label: 'Telefone/WhatsApp' },
  { key: '{{pedido_id}}', label: 'Nº do pedido' },
  { key: '{{status}}', label: 'Status (código)' },
  { key: '{{status_label}}', label: 'Status (rótulo)' },
  { key: '{{total}}', label: 'Total do pedido' },
  { key: '{{estabelecimento}}', label: 'Nome do estabelecimento' },
];
const VARIABLE_LABEL = Object.fromEntries(VARIABLES.map((v) => [v.key, v.label]));

const ACCENT_BY_SIDEBAR = {
  [backgroundColors.primary]: {
    alert: 'primary',
    text: 'text-primary',
    hex: '#7c3aed',
    softBg: 'rgba(124, 58, 237, 0.12)',
    softBorder: 'rgba(124, 58, 237, 0.2)',
  },
  [backgroundColors.blue]: {
    alert: 'info',
    text: 'text-info',
    hex: '#2563eb',
    softBg: 'rgba(37, 99, 235, 0.12)',
    softBorder: 'rgba(37, 99, 235, 0.2)',
  },
  [backgroundColors.green]: {
    alert: 'success',
    text: 'text-success',
    hex: '#22d3ee',
    softBg: 'rgba(34, 211, 238, 0.12)',
    softBorder: 'rgba(34, 211, 238, 0.2)',
  },
};

const normalText = { textTransform: 'none', letterSpacing: 'normal' };

const TEMPLATES_MANAGER_URL = 'https://business.facebook.com/wa/manage/message-templates/';
const FIXED = '__fixed';
const TEMPLATE_STATUS_LABEL = {
  PENDING: 'em análise',
  IN_APPEAL: 'em recurso',
  REJECTED: 'rejeitado',
  PAUSED: 'pausado pela Meta',
  DISABLED: 'desativado pela Meta',
};

const templateKey = (name, language) => (name ? `${name}|${language || ''}` : '');
const unavailableReason = (t) =>
  t.status !== 'APPROVED' ? TEMPLATE_STATUS_LABEL[t.status] || t.status.toLowerCase() : t.unsupportedReason;

const buildEmptySlots = (statuses) => {
  const slots = [];
  (statuses || []).forEach((s) => {
    slots.push({ status: s.status, slot: 1, text: '', active: false, label: s.label, params: {}, fixedKeys: [] });
    slots.push({ status: s.status, slot: 2, text: '', active: false, label: s.label, params: {}, fixedKeys: [] });
  });
  return slots;
};

const mergeMessages = (statuses, saved) => {
  const base = buildEmptySlots(statuses);
  const map = new Map((saved || []).map((m) => [`${m.status}-${m.slot}`, m]));
  return base.map((item) => {
    const found = map.get(`${item.status}-${item.slot}`);
    if (!found) return item;
    const params = found.metaTemplateParams || {};
    return {
      ...item,
      text: found.text || '',
      active: found.active ?? false,
      templateKey: templateKey(found.metaTemplateName, found.metaTemplateLanguage),
      params,
      fixedKeys: Object.keys(params).filter((k) => !VARIABLE_LABEL[params[k]]),
    };
  });
};

/** Erro do slot WABA ativo, ou null. */
const wabaError = (m, template) => {
  if (!m.active) return null;
  if (!m.templateKey) return 'Escolha um template';
  if (!template) return 'Template não encontrado na Meta. Escolha outro.';
  const reason = unavailableReason(template);
  if (reason) return `Template indisponível (${reason}). Escolha outro.`;
  const missing = template.variables.filter((v) => !(m.params[v] || '').trim());
  if (missing.length) return `Preencha ${missing.map((v) => `{{${v}}}`).join(', ')}`;
  return null;
};

function WhatsappMessagesSettings() {
  const { account } = useContext(ApiContext);
  const { color: sidebarColor } = useContext(BackgroundColorContext);
  const accent = ACCENT_BY_SIDEBAR[sidebarColor] || ACCENT_BY_SIDEBAR[backgroundColors.primary];
  const { notify } = useNotification();
  const isWaba = account?.whatsappProvider === 'waba';
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');

  const templatesByKey = useMemo(
    () => new Map(templates.map((t) => [templateKey(t.name, t.language), t])),
    [templates],
  );
  const templateGroups = useMemo(() => {
    const available = templates.filter((t) => !unavailableReason(t));
    const unavailable = templates.filter((t) => unavailableReason(t));
    return [
      { label: 'Disponíveis', items: available },
      { label: 'Indisponíveis', items: unavailable },
    ].filter((g) => g.items.length);
  }, [templates]);
  const showLanguage = useMemo(
    () => new Set(templates.map((t) => t.name)).size !== templates.length,
    [templates],
  );

  const loadTemplates = async () => {
    if (!account?.id) return;
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      setTemplates((await WhatsappService.getCloudTemplates(account.id)) || []);
    } catch (err) {
      console.error(err);
      setTemplatesError(err?.response?.data?.message || 'Não foi possível buscar os templates na Meta.');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const load = async () => {
    if (!account?.id) return;
    setLoading(true);
    try {
      const [statusList, messages] = await Promise.all([
        AccountService.getOrderStatuses(account.id),
        AccountService.getWhatsappMessages(account.id),
      ]);
      const sorted = (statusList || []).slice().sort((a, b) => a.order - b.order);
      const active = sorted.filter((s) => s.active);
      setStatuses(active);
      setItems(mergeMessages(active, messages));
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar mensagens', 'icon-alert-circle-exc');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (isWaba) loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, isWaba]);

  const updateItem = (status, slot, patch) => {
    setItems((prev) =>
      prev.map((i) => (i.status === status && i.slot === slot ? { ...i, ...patch } : i)),
    );
  };

  const insertVariable = (status, slot, variable) => {
    setItems((prev) =>
      prev.map((i) =>
        i.status === status && i.slot === slot ? { ...i, text: `${i.text}${variable}` } : i,
      ),
    );
  };

  const selectTemplate = (m, key) => {
    const template = templatesByKey.get(key);
    // Variável com o mesmo nome de uma do pedido já vem preenchida; o resto mantém o que havia.
    const params = Object.fromEntries(
      (template?.variables || []).map((v) => [
        v,
        m.params[v] ?? (VARIABLE_LABEL[`{{${v}}}`] ? `{{${v}}}` : ''),
      ]),
    );
    updateItem(m.status, m.slot, {
      templateKey: key,
      params,
      fixedKeys: m.fixedKeys.filter((k) => k in params),
    });
  };

  const setParamSource = (m, variable, source) => {
    const fixed = source === FIXED;
    updateItem(m.status, m.slot, {
      params: { ...m.params, [variable]: fixed ? '' : source },
      fixedKeys: fixed ? [...new Set([...m.fixedKeys, variable])] : m.fixedKeys.filter((k) => k !== variable),
    });
  };

  const errors = useMemo(
    () =>
      isWaba
        ? new Map(items.map((m) => [`${m.status}-${m.slot}`, wabaError(m, templatesByKey.get(m.templateKey))]))
        : new Map(),
    [isWaba, items, templatesByKey],
  );
  const hasInvalid = [...errors.values()].some(Boolean);
  const blockedByTemplates = isWaba && (templatesLoading || !!templatesError);

  const handleSave = async () => {
    if (hasInvalid) {
      notify('tr', 'warning', 'Corrija as mensagens marcadas antes de salvar', 'icon-alert-circle-exc');
      return;
    }
    setSaving(true);
    try {
      const payload = items.map((i) => {
        if (!isWaba) return { status: i.status, slot: i.slot, text: i.text || '', active: i.active };
        const template = templatesByKey.get(i.templateKey);
        return {
          status: i.status,
          slot: i.slot,
          active: i.active,
          metaTemplateName: template?.name,
          metaTemplateLanguage: template?.language,
          metaTemplateParams: template
            ? Object.fromEntries(template.variables.map((v) => [v, i.params[v] || '']))
            : undefined,
        };
      });
      const saved = await AccountService.updateWhatsappMessages(account.id, payload);
      setItems(mergeMessages(statuses, saved));
      notify('tr', 'success', 'Mensagens salvas!', 'icon-check-2');
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar mensagens',
        'icon-alert-circle-exc',
      );
    } finally {
      setSaving(false);
    }
  };

  const grouped = statuses.map((s) => ({
    status: s,
    messages: items.filter((i) => i.status === s.status),
  }));

  const renderPreview = (template, m) => {
    const parts = template.body.split(/(\{\{\s*\w+\s*\}\})/g);
    return (
      <div
        className="mt-2"
        style={{
          ...normalText,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.6,
          fontSize: 14,
          padding: '10px 12px',
          borderRadius: 8,
          background: accent.softBg,
          border: `1px solid ${accent.softBorder}`,
          opacity: m.active ? 1 : 0.6,
        }}
      >
        {parts.map((part, idx) => {
          const variable = part.match(/^\{\{\s*(\w+)\s*\}\}$/)?.[1];
          if (!variable) return <React.Fragment key={idx}>{part}</React.Fragment>;
          const value = (m.params[variable] || '').trim();
          const filled = !!value;
          return (
            <span
              key={idx}
              title={`{{${variable}}}`}
              style={{
                padding: '1px 6px',
                borderRadius: 4,
                fontWeight: 600,
                background: filled ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 178, 43, 0.2)',
                color: filled ? undefined : '#ff8d72',
              }}
            >
              {filled ? VARIABLE_LABEL[value] || value : `{{${variable}}}`}
            </span>
          );
        })}
      </div>
    );
  };

  const renderWabaCell = (m) => {
    const template = templatesByKey.get(m.templateKey);
    const error = errors.get(`${m.status}-${m.slot}`);
    const savedMissing = m.templateKey && !template && !templatesLoading && !templatesError;
    return (
      <td>
        <Input
          type="select"
          value={m.templateKey || ''}
          disabled={!m.active || templatesLoading || !!templatesError}
          invalid={!!error && !templatesLoading}
          style={normalText}
          onChange={(e) => selectTemplate(m, e.target.value)}
        >
          <option value="">{templatesLoading ? 'Carregando templates...' : 'Selecione um template aprovado'}</option>
          {savedMissing && <option value={m.templateKey}>{m.templateKey.split('|')[0]} (não encontrado)</option>}
          {templateGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.items.map((t) => {
                const reason = unavailableReason(t);
                const key = templateKey(t.name, t.language);
                return (
                  <option key={key} value={key} disabled={!!reason && key !== m.templateKey}>
                    {t.name}
                    {showLanguage ? ` · ${t.language}` : ''}
                    {t.variables.length ? ` · ${t.variables.length} variáve${t.variables.length > 1 ? 'is' : 'l'}` : ''}
                    {reason ? ` (${reason})` : ''}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </Input>

        {template && renderPreview(template, m)}

        {template && template.variables.length > 0 && (
          <div className="mt-3">
            {template.variables.map((v) => {
              const value = m.params[v] ?? '';
              const isFixed = m.fixedKeys.includes(v) || (value && !VARIABLE_LABEL[value]);
              return (
                <Row key={v} className="align-items-center mb-2" style={normalText}>
                  <Col sm="3" className="mb-1 mb-sm-0">
                    <code style={{ fontSize: 13 }}>{`{{${v}}}`}</code>
                  </Col>
                  <Col sm={isFixed ? 4 : 9} className="mb-1 mb-sm-0">
                    <Input
                      type="select"
                      bsSize="sm"
                      value={isFixed ? FIXED : value}
                      disabled={!m.active}
                      invalid={m.active && !value.trim()}
                      style={normalText}
                      onChange={(e) => setParamSource(m, v, e.target.value)}
                    >
                      <option value="">Escolha o que enviar</option>
                      {VARIABLES.map((opt) => (
                        <option key={opt.key} value={opt.key}>
                          {opt.label}
                        </option>
                      ))}
                      <option value={FIXED}>Texto fixo</option>
                    </Input>
                  </Col>
                  {isFixed && (
                    <Col sm="5">
                      <Input
                        bsSize="sm"
                        value={value}
                        disabled={!m.active}
                        maxLength={200}
                        placeholder="Digite o texto"
                        invalid={m.active && !value.trim()}
                        onChange={(e) => updateItem(m.status, m.slot, { params: { ...m.params, [v]: e.target.value } })}
                      />
                    </Col>
                  )}
                </Row>
              );
            })}
          </div>
        )}

        {error && !templatesLoading && !templatesError && (
          <div className="text-danger mt-2" style={{ ...normalText, fontSize: 13 }}>
            {error}
          </div>
        )}
      </td>
    );
  };

  const renderTextCell = (m, status) => (
    <td>
      <Input
        type="textarea"
        rows={3}
        value={m.text}
        disabled={!m.active}
        placeholder={`Mensagem ${m.slot} para ${status.label}`}
        style={{ resize: 'none', overflow: 'hidden' }}
        onChange={(e) => updateItem(m.status, m.slot, { text: e.target.value })}
      />
      <div className="mt-2" style={normalText}>
        <span className="text-muted mr-2">Inserir:</span>
        {VARIABLES.map((v) => (
          <Button
            key={v.key}
            size="sm"
            color={accent.alert}
            className="btn-link p-0 mr-3"
            style={normalText}
            disabled={!m.active}
            onClick={() => insertVariable(m.status, m.slot, v.key)}
          >
            {v.label}
          </Button>
        ))}
      </div>
    </td>
  );

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="d-flex align-items-start justify-content-between flex-wrap" style={{ gap: 12 }}>
              <div>
                <CardTitle tag="h4">Mensagens WhatsApp</CardTitle>
                <p className="card-category mb-0" style={normalText}>
                  {isWaba
                    ? 'Escolha até 2 templates por status. Mensagens inativas não são enviadas.'
                    : 'Configure até 2 mensagens por status. Mensagens vazias ou inativas não serão enviadas.'}
                </p>
              </div>
              {isWaba && (
                <Button size="sm" color="info" outline onClick={loadTemplates} disabled={templatesLoading || saving}>
                  {templatesLoading ? 'Atualizando...' : 'Atualizar templates'}
                </Button>
              )}
            </CardHeader>
            <CardBody>
              {isWaba ? (
                <Alert color={accent.alert} className="mb-4" style={normalText}>
                  <strong>WhatsApp oficial: as mensagens usam templates aprovados pela Meta.</strong>
                  <div className="mt-1">
                    Crie e acompanhe a aprovação no{' '}
                    <a
                      href={TEMPLATES_MANAGER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
                    >
                      WhatsApp Manager
                    </a>
                    . Aqui você escolhe o template de cada status e o que vai em cada variável. Depois de
                    criar um template, clique em “Atualizar templates”.
                  </div>
                </Alert>
              ) : (
                <Alert color={accent.alert} className="mb-4" style={normalText}>
                  <strong>Variáveis disponíveis</strong>
                  <div className="mt-2 d-flex flex-wrap" style={{ gap: 6 }}>
                    {VARIABLES.map((v) => (
                      <Badge
                        key={v.key}
                        color={accent.alert}
                        pill
                        style={{
                          ...normalText,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          padding: '0.4em 0.75em',
                          backgroundColor: 'rgba(0, 0, 0, 0.15)',
                        }}
                      >
                        {v.key}
                      </Badge>
                    ))}
                  </div>
                </Alert>
              )}

              {isWaba && templatesError && (
                <Alert color="danger" className="mb-4 d-flex align-items-center justify-content-between flex-wrap" style={{ ...normalText, gap: 12 }}>
                  <span>{templatesError}</span>
                  <Button size="sm" color="neutral" onClick={loadTemplates}>
                    Tentar novamente
                  </Button>
                </Alert>
              )}

              {isWaba && !templatesLoading && !templatesError && templates.length === 0 && (
                <Alert color="warning" className="mb-4" style={normalText}>
                  Nenhum template encontrado na conta do WhatsApp Business. Crie um no WhatsApp Manager e aguarde a
                  aprovação.
                </Alert>
              )}

              {loading && <p style={normalText}>Carregando...</p>}

              {!loading &&
                grouped.map(({ status, messages }) => (
                  <Card
                    key={status.status}
                    className="mb-4"
                    style={{ borderLeft: `4px solid ${accent.hex}`, overflow: 'hidden' }}
                  >
                    <CardHeader
                      style={{
                        background: accent.softBg,
                        padding: '12px 20px',
                        borderBottom: `1px solid ${accent.softBorder}`,
                      }}
                    >
                      <h5 className={`mb-0 ${accent.text}`} style={{ ...normalText, fontWeight: 600 }}>
                        {status.label}
                      </h5>
                    </CardHeader>
                    <CardBody className="pt-3 pb-2">
                      <Table className="mb-0">
                        <thead className={accent.text}>
                          <tr style={normalText}>
                            <th style={{ width: 50 }}>#</th>
                            <th style={{ width: 90 }}>Ativo</th>
                            <th>{isWaba ? 'Template' : 'Mensagem'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {messages.map((m) => (
                            <tr key={`${m.status}-${m.slot}`}>
                              <td className="align-top pt-3">{m.slot}</td>
                              <td className="align-top pt-3">
                                <FormGroup check className="mb-0">
                                  <Label check className="mb-0" style={normalText}>
                                    <Input
                                      type="checkbox"
                                      checked={m.active}
                                      onChange={(e) => updateItem(m.status, m.slot, { active: e.target.checked })}
                                    />
                                    <span className="form-check-sign">
                                      <span className="check" />
                                    </span>
                                  </Label>
                                </FormGroup>
                              </td>
                              {isWaba ? renderWabaCell(m) : renderTextCell(m, status)}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                ))}

              {!loading && grouped.length === 0 && (
                <Alert color="warning" className="mb-0" style={normalText}>
                  Nenhum status ativo encontrado. Ative status em Configurações → Status do pedido.
                </Alert>
              )}
            </CardBody>
            <CardFooter>
              <Button
                className="btn-fill"
                color="primary"
                onClick={handleSave}
                disabled={saving || loading || hasInvalid || blockedByTemplates}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default WhatsappMessagesSettings;
